import { Eye, EyeOff } from 'lucide-react'
import { useState, type InputHTMLAttributes, type ReactNode, type Ref } from 'react'
import { cn } from './cn'
import { useFieldControl } from './field-context'
import { t } from '@/lib/i18n'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  /** Icon or text shown inside the field before the value, for example "mgai.school/". */
  prefix?: ReactNode
  /** Icon, text or small button shown after the value. */
  suffix?: ReactNode
  inputSize?: 'sm' | 'md'
  invalid?: boolean
  /** Class for the outer box (border, background). `className` goes on the <input>. */
  wrapperClassName?: string
  ref?: Ref<HTMLInputElement>
}

/** Shared box styles for text like controls (Input, Select trigger, Combobox). */
export const controlBox =
  'flex w-full items-center rounded-control border border-line-strong bg-surface text-body text-text ' +
  'transition-[border-color,box-shadow] duration-150 ease-out ' +
  'focus-within:border-focus focus-within:ring-3 focus-within:ring-focus-halo ' +
  'data-[invalid=true]:border-danger data-[invalid=true]:focus-within:ring-danger/20 ' +
  'data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-50'

/** Text input with label wiring (inside <Field>), prefix and suffix slots and password show and hide. */
export function Input({
  prefix,
  suffix,
  inputSize = 'md',
  invalid,
  wrapperClassName,
  className,
  type = 'text',
  id,
  disabled,
  ref,
  ...rest
}: InputProps) {
  const control = useFieldControl({
    id,
    disabled,
    'aria-describedby': rest['aria-describedby'],
    'aria-invalid': invalid ?? (rest['aria-invalid'] as boolean | undefined),
  })
  const [reveal, setReveal] = useState(false)
  const isPassword = type === 'password'
  const textPrefix = typeof prefix === 'string'
  return (
    <div
      className={cn(controlBox, inputSize === 'sm' ? 'h-8' : 'h-10', wrapperClassName)}
      data-invalid={control.invalid || undefined}
      data-disabled={control.disabled || undefined}
    >
      {prefix != null && (
        <span
          className={cn(
            'flex shrink-0 items-center pl-3 text-muted select-none [&_svg]:size-[18px] [&_svg]:stroke-[1.75]',
            textPrefix ? 'pr-0 whitespace-nowrap' : 'pr-0.5',
          )}
        >
          {prefix}
        </span>
      )}
      <input
        ref={ref}
        type={isPassword && reveal ? 'text' : type}
        {...rest}
        id={control.id}
        disabled={control.disabled}
        aria-invalid={control['aria-invalid']}
        aria-describedby={control['aria-describedby']}
        aria-required={control['aria-required']}
        className={cn(
          'h-full w-full min-w-0 flex-1 rounded-control bg-transparent px-3 text-text shadow-none outline-none',
          'placeholder:text-subtle disabled:cursor-not-allowed',
          prefix != null && (textPrefix ? 'pl-0' : 'pl-2'),
          (suffix != null || isPassword) && 'pr-2',
          className,
        )}
      />
      {isPassword && (
        <button
          type="button"
          onClick={() => setReveal((v) => !v)}
          aria-label={reveal ? t('Hide password') : t('Show password')}
          aria-pressed={reveal}
          disabled={control.disabled}
          className="mr-1 inline-flex size-8 shrink-0 items-center justify-center rounded-sm text-muted transition-colors hover:bg-surface-2 hover:text-text [&_svg]:size-[18px]"
        >
          {reveal ? <EyeOff strokeWidth={1.75} /> : <Eye strokeWidth={1.75} />}
        </button>
      )}
      {suffix != null && (
        <span className="flex shrink-0 items-center pr-3 text-muted [&_svg]:size-[18px] [&_svg]:stroke-[1.75]">{suffix}</span>
      )}
    </div>
  )
}
